import { memo, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { differenceInYears } from 'date-fns';
import { parseDateV2 } from '@cyren/common-lib';

import { V2QsDateDobProps } from './types';

const DATE_FORMAT = '';
const formatDateInput = (rawValue: string) => {
  const digits = String(rawValue || '').replace(/\D/g, '').slice(0, 8);

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }

  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

/**
 * V2QsDateDob 컴포넌트
 *
 * 생년월일 입력을 위한 마스크 입력 컴포넌트 (MM/DD/YYYY 형식)
 * V1 QsInputDateDob의 V2 버전으로 Figma 디자인 시스템 적용
 *
 * @example
 * ```tsx
 * <V2QsDateDob
 *   value={birthDate}
 *   onChange={setBirthDate}
 *   ageRestriction={18}
 * />
 * ```
 */
function V2QsDateDob({
  value,
  onChange,
  error = false,
  errorMessage,
  disabled = false,
  readOnly = false,
  fullWidth = true,
  placeholder = DATE_FORMAT,
  ageRestriction,
  className,
  containerClassName,
  label,
  hint = 'Month / Day / Year',
  ageRestrictionTitle = 'Age Restriction',
  ageRestrictionMessage = 'You must be at least {age} years old to use this application.',
  ageRestrictionCloseText = 'Close',
}: V2QsDateDobProps) {
  const [dateInput, setDateInput] = useState(value || '');
  const [age, setAge] = useState<number | null>(null);
  const [showAgeWarning, setShowAgeWarning] = useState(false);
  const onChangeRef = useRef(onChange);
  const lastEmittedValueRef = useRef<string | null>(null);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // 값이 외부에서 변경될 때 동기화
  useEffect(() => {
    setDateInput(value || '');
  }, [value]);

  // 날짜 유효성 검증 및 onChange 호출
  useEffect(() => {
    const validDate = parseDateV2({
      dateStr: dateInput,
      formatStr: 'MM/dd/yyyy',
    });

    const digitsCount = dateInput.replace(/\D/g, '').length;
    let nextValue: string | null = null;
    if (validDate && digitsCount === 8) {
      nextValue = dateInput;
    } else if (digitsCount === 0) {
      nextValue = '';
    }

    if (nextValue == null) return;
    if (lastEmittedValueRef.current !== nextValue) {
      lastEmittedValueRef.current = nextValue;
      onChangeRef.current?.(nextValue);
    }

    if (validDate) {
      if (ageRestriction) {
        const calculatedAge = differenceInYears(new Date(), validDate);
        setAge(calculatedAge);
      }
    } else {
      setAge(null);
    }
  }, [dateInput, ageRestriction]);

  // Container 클래스
  const containerClasses = classNames(
    'flex flex-col gap-1',
    containerClassName
  );

  // Input wrapper 클래스
  const wrapperClasses = classNames(
    'flex items-center transition-all duration-200 rounded-[10px]',
    {
      // Width
      'w-full': fullWidth,
      'w-[200px]': !fullWidth,

      // Padding
      'px-4 py-3': true,
      'gap-3': true,

      // Background
      'bg-white': !disabled,
      'bg-[#F2F4FC]': disabled,

      // Border
      'border-[1.5px]': true,
      'border-[#E6E9F5]': !error && !disabled,
      'border-[#FF5E71]': error,
      'hover:border-[#DADDEB]': !disabled && !error,
      'focus-within:border-[#DADDEB]': !disabled && !error,
      'focus-within:bg-[#FAFBFF]': !disabled && !error,

      // Cursor
      'cursor-not-allowed': disabled,
    }
  );

  // Input 클래스
  const inputClasses = classNames(
    'w-full outline-none bg-transparent text-left tracking-widest',
    'text-[15px] font-medium',
    {
      'text-[#1D1D1F]': !disabled,
      'text-[#C3C7D9]': disabled,
      'placeholder:text-[#AFB2C2]': true,
      'cursor-not-allowed': disabled,
    },
    className
  );

  // 라벨 클래스
  const labelClasses = 'text-sm font-medium text-[#222638] mb-1';

  // 힌트 클래스
  const hintClasses = 'text-xs text-[#AFB2C2]';

  // 에러 메시지 클래스
  const errorClasses = 'text-xs text-[#FF5E71]';

  // 나이 제한 경고 모달
  const handleBlur = () => {
    if (ageRestriction && age != null && age < ageRestriction) {
      setShowAgeWarning(true);
    }
  };

  return (
    <div className={containerClasses}>
      {/* Label */}
      {label && <label className={labelClasses}>{label}</label>}

      {/* Input Wrapper */}
      <div className={wrapperClasses}>
        <input
          type="text"
          className={inputClasses}
          value={dateInput}
          onChange={(e) => setDateInput(formatDateInput(e.target.value))}
          pattern="[0-9]*"
          inputMode="numeric"
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={10}
        />
      </div>

      {/* Hint */}
      {hint && !error && <span className={hintClasses}>{hint}</span>}

      {/* Error Message */}
      {error && errorMessage && (
        <span className={errorClasses}>{errorMessage}</span>
      )}

      {/* Age Restriction Warning Modal */}
      {showAgeWarning && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowAgeWarning(false)}
        >
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center">
            <h3 className="text-xl font-bold text-[#222638] mb-4">
              {ageRestrictionTitle}
            </h3>
            <p className="text-[#737580]">
              {ageRestrictionMessage.replace('{age}', String(ageRestriction))}
            </p>
            <button
              className="mt-6 px-6 py-2 bg-[#222638] text-white rounded-lg font-medium"
              onClick={() => setShowAgeWarning(false)}
            >
              {ageRestrictionCloseText}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(V2QsDateDob);
